import React, {
  useContext,
  useState,
  useEffect,
  Fragment,
  useRef
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'

import { StepHandlerContext } from '../../contexts/stepHandler.context'
import { NextButtonAvailabilityContext } from '../../contexts/nextButtonAvailability.context'
import { windowAmplitudeTrack } from '../../../shared/helpers/utils'

import {
  setSubscriptionVariants,
  setSubscriptionMix,
  setSubscriptionStep,
  setSubscriptionSizeSubSteps,
  setSubscriptionPatternSubSteps,
  goToBundle
} from '../../store/subscription/subscription.action'
import {
  selectSubscriptionBundle,
  selectSubscriptionMix,
  selectSubscriptionStep,
  selectSubscriptionSizeSubSteps,
  selectSubscriptionVariants
} from '../../store/subscription/subscription.selector'
import {
  SHOP_COUCHE_PATH,
  BUNDLE_STEP,
  PATTERN_STEP,
  DIAPERS_BUNDLE,
  PANTS_BUNDLE,
  MIX_BUNDLE,
  PANTS_PRODUCT_TYPE,
  DIAPERS_PRODUCT_TYPE,
  DIAPERS_SIZES,
  PANTS_SIZES
} from '../../utils/bab/bab.utils'

import Title from '../../components/title/title.component'
import Text from '../../components/text/text.component'
import SizeMix from '../../components/size-mix/size-mix.component'
import TypeGrid from '../../components/type-grid/type-grid.component'
import SizeGrid from '../../components/size-grid/size-grid.component'
import PopinWrapper from '../../components/popin-wrapper/popin-wrapper.component'
import DiaperSizeGuide from '../../../shared/components/atoms/DiaperSizeGuide'

import './size.styles.scss'

const Size = () => {
  const { t } = useTranslation('updatedBAB')
  const dispatch = useDispatch()
  const history = useHistory()

  const [localSizes, setLocalSizes] = useState({})
  const localSizesRef = useRef(localSizes)

  const { setPreviousStep, setNextStep } = useContext(StepHandlerContext)
  const { nextButtonAvailability, setNextButtonAvailability } = useContext(
    NextButtonAvailabilityContext
  )

  const subscriptionBundle = useSelector(selectSubscriptionBundle)
  const subscriptionVariants = useSelector(selectSubscriptionVariants)
  const subscriptionMix = useSelector(selectSubscriptionMix)
  const subscriptionStep = useSelector(selectSubscriptionStep)
  const subSteps = useSelector(selectSubscriptionSizeSubSteps)

  const completedSubSteps = subSteps.completed

  const isDiapersBundle = subscriptionBundle === DIAPERS_BUNDLE.id
  const isPantsBundle = subscriptionBundle === PANTS_BUNDLE.id
  const isMixBundle = subscriptionBundle === MIX_BUNDLE.id

  const productTypeData =
    (isMixBundle && completedSubSteps === 0) || isPantsBundle
      ? PANTS_PRODUCT_TYPE
      : DIAPERS_PRODUCT_TYPE

  const productSizes =
    (isMixBundle && completedSubSteps === 0) || isPantsBundle
      ? PANTS_SIZES
      : DIAPERS_SIZES

  let productMaxQuantity

  if (isDiapersBundle) {
    productMaxQuantity = DIAPERS_BUNDLE.productTypes.diapers
  } else if (isPantsBundle) {
    productMaxQuantity = PANTS_BUNDLE.productTypes.pants
  } else if (isMixBundle) {
    if (completedSubSteps === 1) {
      productMaxQuantity = MIX_BUNDLE.productTypes.diapers
    } else {
      productMaxQuantity = MIX_BUNDLE.productTypes.pants
    }
  }

  let typeGridData = []

  if (!isPantsBundle) {
    typeGridData.push({
      id: 1,
      productType: PANTS_PRODUCT_TYPE.id,
      translationKeys: {
        productName: PANTS_PRODUCT_TYPE.translationKeys.singularName
      }
    })
  }

  if (!isDiapersBundle) {
    typeGridData.push({
      id: 0,
      productType: DIAPERS_PRODUCT_TYPE.id,
      translationKeys: {
        productName: DIAPERS_PRODUCT_TYPE.translationKeys.singularName
      }
    })
  }

  // Cycle de vie composant
  useEffect(() => {
    const previousAvailability = nextButtonAvailability
    setPreviousStep(() => handlePreviousStep)
    setNextStep(() => handleNextStep)
    setNextButtonAvailability(false)

    return () => {
      setLocalSizes({})
      setPreviousStep(() => {})
      setNextStep(() => {})
      setNextButtonAvailability(previousAvailability)
    }
  }, [subscriptionMix, subscriptionStep, subSteps])

  // Disponibilité du bouton suivant
  useEffect(() => {
    const numberOfSelectedProducts = Object.values(localSizes).reduce(
      (acc, sizeArray) => acc + sizeArray.length,
      0
    )
    localSizesRef.current = localSizes

    if (numberOfSelectedProducts === productMaxQuantity) {
      isMixBundle && completedSubSteps === 0
        ? handleNextStep()
        : setNextButtonAvailability(true)
    } else {
      setNextButtonAvailability(false)
    }
  }, [localSizes])

  // Clic sur le bouton précédent
  const handlePreviousStep = () => {
    dispatch(setSubscriptionVariants({}))

    if (isDiapersBundle) {
      window.open(SHOP_COUCHE_PATH, '_self')
    } else if (isMixBundle && completedSubSteps === 1) {
      dispatch(setSubscriptionSizeSubSteps(1, 0))
    } else {
      dispatch(goToBundle())
      history.replace(BUNDLE_STEP.path)
    }
  }

  // Clic sur le bouton suivant
  const handleNextStep = () => {
    windowAmplitudeTrack('BabDiaperSizeDone')
    let updatedProductSizes = localSizesRef.current
    dispatch(
      setSubscriptionVariants({
        ...subscriptionVariants,
        ...updatedProductSizes
      })
    )

    let productSizesCount = Object.entries(updatedProductSizes).length

    if (isMixBundle) {
      dispatch(setSubscriptionPatternSubSteps(1, 0))
    } else {
      dispatch(setSubscriptionPatternSubSteps(productSizesCount - 1, 0))
    }

    if (isMixBundle && completedSubSteps === 0) {
      dispatch(setSubscriptionSizeSubSteps(1, 1))
    } else {
      dispatch(setSubscriptionStep(2))
      history.replace(PATTERN_STEP.path)
    }
  }

  // Gestion du changement de l'état Mix
  const handleSizeMixChange = (event) => {
    const targetElement = event.target
    const noMixElement = targetElement.closest('.size-mix-button[data-no]')
    const mixElement = targetElement.closest('.size-mix-button[data-yes]')

    if (!noMixElement && !mixElement) return false

    if (mixElement) dispatch(setSubscriptionMix(true))
    else dispatch(setSubscriptionMix(false))
  }

  // Gestion du changement des tailles (état local)
  const handleSizeChange = (event) => {
    const targetElement = event.target
    const container = targetElement.closest('.size-selector-container')
    const sizeButton = targetElement.closest('.size-selector')
    const minusButton = targetElement.closest('.size-choice-minus')
    const plusButton = targetElement.closest('.size-choice-plus')

    if (!sizeButton && !minusButton && !plusButton) return false

    const sizeId = container.dataset.id

    const associatedQuantity =
      (localSizes[sizeId] && localSizes[sizeId].length) || 0

    const totalQuantity = Object.values(localSizes).reduce(
      (acc, sizeArray) => acc + sizeArray.length,
      0
    )

    if (sizeButton) {
      setLocalSizes({
        [sizeId]: Array.from({ length: productMaxQuantity }).fill('')
      })
    } else if (minusButton && associatedQuantity > 0) {
      setLocalSizes((prevState) => {
        const newSizeArray = [...prevState[sizeId]]

        if (associatedQuantity === 1) {
          const newState = { ...prevState }
          delete newState[sizeId]
          return newState
        } else {
          newSizeArray.pop()
          return { ...prevState, [sizeId]: newSizeArray }
        }
      })
    } else if (plusButton && totalQuantity < productMaxQuantity) {
      setLocalSizes((prevState) => {
        const newSizeArray = prevState[sizeId] ? [...prevState[sizeId]] : []
        newSizeArray.push('')
        return { ...prevState, [sizeId]: newSizeArray }
      })
    }
  }

  // Gestion de l'ouverture du guide de tailles
  const openSizeGuide = () => {
    document.querySelector('#size-guide').setAttribute('data-open', '')
  }

  // Gestion du départ à la route BUNDLE
  const toBundleRoute = () => {
    dispatch(setSubscriptionVariants({}))
    dispatch(goToBundle())
    history.replace(BUNDLE_STEP.path)
  }

  return (
    <Fragment>
      <Helmet>
        <title>{t('size.metaData.title')}</title>
        <meta name='description' content={t('size.metaData.description')} />
      </Helmet>
      <Title
        content={t('size.title', {
          returnObjects: true,
          quantity: productMaxQuantity,
          productName: t(productTypeData.translationKeys.lowerCaseName)
        })}
        quantity={productMaxQuantity}
      />

      {!isMixBundle ? (
        <SizeMix
          margins={[32, 0, 32, 0]}
          mix={subscriptionMix}
          clickHandler={handleSizeMixChange}
        />
      ) : (
        <TypeGrid
          typeGridData={typeGridData}
          step={subscriptionStep}
          completedSubSteps={completedSubSteps}
          margins={[16, 0, 16, 0]}
        />
      )}

      <SizeGrid
        sizesData={productSizes}
        localSizes={localSizes}
        productType={productTypeData.id}
        mix={subscriptionMix}
        productMaxQuantity={productMaxQuantity}
        margins={[32, 0, 32, 0]}
        clickHandler={handleSizeChange}
      />

      <Text
        content={t('size.sizeGuide.heading')}
        classNames='size-guide-heading'
        margins={[32, 0, 32, 0]}
        clickHandler={openSizeGuide}
      />
      <Text
        content={t('size.sizeGuide.text')}
        classNames={'size-guide-text'}
        margins={[12, 0, 24, 0]}
      />

      {isDiapersBundle && (
        <Text
          content={t('size.selectDiaper')}
          classNames={'select-diaper'}
          margins={[32, 0, 32, 0]}
          clickHandler={toBundleRoute}
        />
      )}
      <PopinWrapper id={'size-guide'}>
        <DiaperSizeGuide />
      </PopinWrapper>
    </Fragment>
  )
}

export default Size
